import React from "react";
import {
  Linking,
  StyleSheet,
  View,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Switch,
} from "react-native";
import CallIcon from "@assets/svg/CircleCall";
import color from "@constants/color";
import handleCallButtonPress from "@utils/handleCallButtonPress";
import Text from "@components/CustomText";
import NoBag from "@assets/svg/NoBag";
import Tag from "@components/Tag";
import PolyBagIcon from "@assets/svg/MyTasks/PolyBagIcon";

const cardKindLabel = (item) => {
  const cardId = item?.trip_card_id;
  if (!cardId) return null;
  if (cardId.startsWith("pending:")) return "Pending";
  if (cardId.startsWith("disabled:")) return null;
  if (item?.trip_id) {
    const tid = String(item.trip_id);
    return tid.length > 10 ? `Trip · …${tid.slice(-6)}` : `Trip · ${tid}`;
  }
  if (cardId.startsWith("trip:") || cardId.startsWith("ofd:")) {
    return "Live trip";
  }
  return null;
};

const UserCard = ({
  item,
  onToggleUserSwitch = () => {},
  isToggleOn = false,
  onPressUnassign = null,
  unassignBusy = false,
}) => {
  const openMapHandler = (lat, lng) => async () => {
    const url = `google.navigation:q=${lat},${lng}`;
    Linking.canOpenURL(url).then((supported) => {
      if (supported) return Linking.openURL(url);
      else {
        const browser_url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
        return Linking.openURL(browser_url);
      }
    });
  };

  const callCustomer = (phoneNumber) => {
    handleCallButtonPress(phoneNumber);
  };

  const itemBags = item?.bag_names || [];
  const kindLabel = cardKindLabel(item);

  return (
    <View
      style={[
        styles.container,
        (item?.is_escalated || item?.is_priority_cx) &&
          styles.highlightedContainer,
      ]}
    >
      <View style={styles.rowContainer}>
        <View style={styles.bagContainer}>
          {item?.show_no_bag_icon ? (
            <NoBag height={32} width={27} />
          ) : (
            itemBags?.map((i, index) => (
              <Text
                key={`${i}-${index}`}
                variant="heading_24"
                numberOfLines={1}
                style={[
                  styles.bagText,
                  index === itemBags.length - 1 && styles.bagTextLast,
                ]}
              >
                {index === itemBags.length - 1 ? i : `${i}, `}
              </Text>
            ))
          )}
        </View>
        <View>
          {item?.is_priority_cx && (
            <View style={styles.priorityTag}>
              <Tag text="Priority" bgcolor="rgba(66, 116, 234, 1)" />
            </View>
          )}
          {!item?.enable_ofd ? (
            <View style={[styles.ofdContainer, styles.ofdContainerDisabled]}>
              <Text variant="caption_1" style={{ color: color.plain_red }}>
                {item?.disabled_reason}
              </Text>
            </View>
          ) : !item?.is_ofd ? (
            <Switch
              trackColor={{
                false: color.darkGrey,
                true: "rgba(193, 232, 173, 1)",
              }}
              thumbColor={isToggleOn ? color.plain_green : color.white}
              onValueChange={() => onToggleUserSwitch(item)}
              value={isToggleOn}
            />
          ) : (
            <View style={styles.ofdContainer}>
              <Text variant="caption_1" style={{ color: color.plain_green }}>
                OFD Marked
              </Text>
            </View>
          )}
        </View>
      </View>
      {item?.disabled_type === "PACKING" ? (
        <Text variant="heading_24" style={styles.notBaggedText}>
          Not Bagged!
        </Text>
      ) : null}
      {item?.plastic_bag_opted_in && (
        <View style={styles.polyBagTagContainer}>
          <Tag
            text="पॉली बैग"
            bgcolor="#FFF9E6"
            textColor="#B45309"
            borderColor="#B45309"
            icon={<PolyBagIcon color="#B45309" width={14} height={14} />}
          />
        </View>
      )}
      {item?.is_ofd && (item?.return_packed_count ?? 0) > 0 ? (
        <View style={styles.polyBagTagContainer}>
          <Tag
            text={
              item.return_packed_count === 1
                ? "Return packed · 1 order"
                : `Return packed · ${item.return_packed_count} orders`
            }
            bgcolor="#EEF2FF"
            textColor="#4338CA"
            borderColor="#4338CA"
          />
        </View>
      ) : null}
      <View style={styles.divider} />
      <View style={styles.rowContainer}>
        <View style={styles.nameContainer}>
          <Text variant="body_text_2" style={styles.userName}>
            {item.user_name}
          </Text>
          {kindLabel ? (
            <View style={styles.tagSpacer}>
              <Tag
                text={kindLabel}
                bgcolor="#EEF2FF"
                textColor="#4338CA"
                borderColor="#4338CA"
              />
            </View>
          ) : null}
          {item?.is_escalated && (
            <View style={styles.tagSpacer}>
              <Tag text="Escalated" bgcolor={color.royalBlue} />
            </View>
          )}
        </View>
        <TouchableWithoutFeedback
          onPress={() => callCustomer(item?.user_phone)}
        >
          <CallIcon />
        </TouchableWithoutFeedback>
      </View>
      <TouchableOpacity
        testID="cpdr-open-map"
        disabled={!(item?.user_lat && item?.user_lng)}
        onPress={openMapHandler(item?.user_lat, item?.user_lng)}
      >
        <Text
          style={[
            styles.addressText,
            item?.user_lat && item?.user_lng && styles.addressLink,
          ]}
        >
          {item.user_address}
        </Text>
      </TouchableOpacity>
      {item?.enable_unassign === true && onPressUnassign ? (
        <TouchableOpacity
          testID="ofd-unassign"
          activeOpacity={0.85}
          disabled={unassignBusy}
          onPress={() => onPressUnassign(item)}
          style={styles.unassignBtn}
        >
          <Text variant="caption_1" style={styles.unassignText}>
            {/* Translated server-side (#28939); English literal kept as the
                mandatory fallback for a backend that predates it. */}
            {item?.unassign_text || "Unassign"}
          </Text>
        </TouchableOpacity>
      ) : null}
      {/* CX_QR_PAYMENT_IN_PROGRESS — Unassign is off while a boy takes a QR
          payment from this customer; greyed and inert, with the backend's
          translated reason under it. */}
      {item?.enable_unassign !== true && item?.unassign_blocked_reason ? (
        <>
          <View
            testID="ofd-unassign-blocked"
            style={[styles.unassignBtn, styles.unassignBtnDisabled]}
          >
            <Text variant="caption_1" style={styles.unassignTextDisabled}>
              {item?.unassign_text || "Unassign"}
            </Text>
          </View>
          <Text variant="caption_1" style={styles.unassignBlockedText}>
            {item.unassign_blocked_reason}
          </Text>
        </>
      ) : null}
    </View>
  );
};

export default UserCard;

const styles = StyleSheet.create({
  container: {
    backgroundColor: color.white,
    padding: 16,
    margin: 16,
    marginVertical: 8,
    borderRadius: 12,
  },
  highlightedContainer: {
    borderWidth: 1,
    borderColor: color.royalBlue,
  },
  tagSpacer: {
    marginLeft: 6,
  },
  rowContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  bagContainer: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    flexWrap: "wrap",
  },
  bagText: {
    color: color.mediumBlack,
  },
  bagTextLast: {
    marginRight: 0,
  },
  ofdContainer: {
    backgroundColor: color.veryLightGreen,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingBottom: 2,
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
  ofdContainerDisabled: {
    backgroundColor: color.lightRed,
  },
  notBaggedText: {
    color: color.plain_red,
    fontSize: 24,
  },
  divider: {
    backgroundColor: color.lighterBlack,
    height: 1,
    width: "100%",
    marginVertical: 8,
  },
  nameContainer: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  userName: {
    color: color.mediumBlack,
  },
  addressText: {
    color: color.mediumBlacklightBlack,
  },
  addressLink: {
    color: color.royalBlue,
    textDecorationLine: "underline",
  },
  priorityTag: {
    marginBottom: 4,
    alignSelf: "flex-start",
  },
  polyBagTagContainer: {
    marginTop: 8,
    alignSelf: "flex-start",
  },
  unassignBtn: {
    marginTop: 10,
    alignSelf: "flex-start",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: color.plain_red,
    backgroundColor: color.lightRed,
  },
  unassignText: {
    color: color.plain_red,
  },
  unassignBtnDisabled: {
    borderColor: color.darkGrey,
    backgroundColor: color.lightgrey2,
  },
  unassignTextDisabled: {
    color: color.darkGrey,
  },
  unassignBlockedText: {
    color: color.light_text_yellow,
    backgroundColor: color.lightYellow,
    marginTop: 8,
    padding: 8,
    borderRadius: 4,
  },
});
